import request from '@/utils/request'
import {
	ElLoading,
	ElMessage
} from 'element-plus'
import router from '@/router' // 需要导入 router

/**
 * 确保 URL 是有效的字符串
 * @param {string} url 
 * @returns {string}
 */
const ensureUrl = (url) => {
	if (!url) return ''
	if (typeof url === 'string') return url
	return String(url)
}

/**
 * 获取请求头，自动添加 token
 * @param {Object} customHeaders - 自定义请求头
 * @returns {Object}
 */
const getHeaders = (customHeaders = {}) => {
	const headers = {
		...customHeaders
	}

	// 从 localStorage 获取 token
	const token = localStorage.getItem('token')
	if (token) {
		headers['token'] = `${token}`
	}

	return headers
}

/**
 * 处理登录过期
 */
const handleUnauthorized = () => {
	// 清除本地存储的登录信息
	localStorage.removeItem('token')
	localStorage.removeItem('user_id')

	// 显示提示消息
	// ElMessage.error('登录已过期，请重新登录')

	// 跳转到登录页
	setTimeout(() => {
		router.push({
			name: 'login'
		})
	}, 1500)
}

/**
 * 通用请求方法
 */
const requestApi = (options) => {
	const {
		url,
		method = 'get',
		params = {},
		data = {},
		dataType = 'json',
		headers = {},
		showLoading = false,
		loadingText = '加载中...',
		fullscreen = true,
		skipAuth = false // 是否跳过登录验证（如登录接口本身）
	} = options

	// 确保 url 是字符串
	const requestUrl = ensureUrl(url)

	if (!requestUrl) {
		console.error('请求 URL 不能为空')
		return Promise.reject(new Error('请求 URL 不能为空'))
	}

	let loadingInstance = null

	// 显示 loading
	if (showLoading && ElLoading) {
		try {
			loadingInstance = ElLoading.service({
				fullscreen: fullscreen,
				text: loadingText,
				background: 'rgba(0, 0, 0, 0.7)',
				lock: true
			})
		} catch (error) {
			console.error('Loading 显示失败：', error)
		}
	}
// 处理请求数据格式
let requestData = data
let requestHeaders = getHeaders(headers) // 自动添加 token

if (['post', 'put', 'patch'].includes(method.toLowerCase())) {
    if (dataType === 'form-data') {
        // 对于 form-data，不要设置 Content-Type，让浏览器自动设置（包含 boundary）
        delete requestHeaders['Content-Type']
        
        // 如果 data 已经是 FormData 对象，直接使用，不要重新创建
        if (data instanceof FormData) {
            requestData = data
        } else {
            const formData = new FormData()
            Object.keys(data).forEach(key => {
                const value = data[key]
                if (value !== undefined && value !== null) {
                    // 处理数组类型
                    if (Array.isArray(value)) {
                        value.forEach(item => {
                            if (item !== undefined && item !== null) {
                                formData.append(`${key}[]`, item)
                            }
                        })
                    } else if (value instanceof File || value instanceof Blob) {
                        formData.append(key, value)
                    } else {
                        formData.append(key, value)
                    }
                }
            })
            requestData = formData
        }
    } else {
        requestHeaders['Content-Type'] = 'application/json'
    }
}

	// 处理 GET 请求的参数
	let requestParams = params
	if (method.toLowerCase() === 'get' && dataType === 'form-data') {
		// 如果是 GET 请求但需要传 FormData，转换为 params
		requestParams = {
			...params,
			...data
		}
		requestData = undefined
	}
	// 发送请求
	return request({
		url: requestUrl,
		method,
		params: requestParams,
		data: requestData,
		headers: requestHeaders
	}).then(res => {
		if (loadingInstance) {
			loadingInstance.close()
		}
		// 检查响应状态码，处理登录过期
		if (res && res.code === 202 && !skipAuth) {
			handleUnauthorized()
			// return Promise.reject(new Error('登录已过期'))
		}

		return res
	}).catch(err => {
		if (loadingInstance) {
			loadingInstance.close()
		}

		// 检查错误响应中的 code
		if (err && err.code === 202 && !skipAuth) {
			handleUnauthorized()
			return Promise.reject(new Error('登录已过期'))
		}

		return Promise.reject(err)
	})
}

/**
 * GET 请求
 * @param {string} url - 请求地址
 * @param {Object} params - URL参数
 * @param {Object} config - 其他配置
 */
export const get = (url, params = {}, config = {}) => {
	return requestApi({
		url,
		method: 'get',
		params,
		...config
	})
}

/**
 * POST 请求
 * @param {string} url - 请求地址
 * @param {Object} data - 请求数据
 * @param {string} dataType - 数据类型 json 或 form-data
 * @param {Object} config - 其他配置
 */
export const post = (url, data = {}, dataType = 'json', config = {}) => {
	return requestApi({
		url,
		method: 'post',
		data,
		dataType,
		...config
	})
}

/**
 * PUT 请求
 */
export const put = (url, data = {}, dataType = 'json', config = {}) => {
	return requestApi({
		url,
		method: 'put',
		data,
		dataType,
		...config
	})
}

/**
 * PATCH 请求
 */
export const patch = (url, data = {}, dataType = 'json', config = {}) => {
	return requestApi({
		url,
		method: 'patch',
		data,
		dataType,
		...config
	})
}

/**
 * DELETE 请求
 */
export const del = (url, params = {}, config = {}) => {
	return requestApi({
		url,
		method: 'delete',
		params,
		...config
	})
}

/**
 * 文件上传
 */
export const uploadFile = (url, data, config = {}) => {
	let formData = data

	// 如果不是 FormData 对象，则创建
	if (!(data instanceof FormData)) {
		formData = new FormData()
		Object.keys(data).forEach(key => {
			const value = data[key]
			if (value !== undefined && value !== null) {
				if (Array.isArray(value)) {
					value.forEach(item => {
						if (item !== undefined && item !== null) {
							formData.append(`${key}[]`, item)
						}
					})
				} else if (value instanceof File || value instanceof Blob) {
					formData.append(key, value)
				} else {
					formData.append(key, value)
				}
			}
		})
	}

	// 合并配置，确保 dataType 为 'form-data'
	const uploadConfig = {
		...config,
		dataType: 'form-data'
	}

	return requestApi({
		url,
		method: 'post',
		data: formData,
		dataType: 'form-data',
		headers: {
			'Content-Type': 'multipart/form-data'
		},
		...uploadConfig
	})
}

/**
 * 单文件上传
 */
export const uploadSingleFile = (url, file, fieldName = 'file', extraData = {}, config = {}) => {
	const formData = new FormData()
	formData.append(fieldName, file)

	// 添加额外数据
	Object.keys(extraData).forEach(key => {
		const value = extraData[key]
		if (value !== undefined && value !== null) {
			formData.append(key, value)
		}
	})

	return uploadFile(url, formData, config)
}

/**
 * 多文件上传
 */
export const uploadMultipleFiles = (url, files, fieldName = 'files', extraData = {}, config = {}) => {
	const formData = new FormData()

	files.forEach((file, index) => {
		if (file) {
			formData.append(`${fieldName}[]`, file)
		}
	})

	Object.keys(extraData).forEach(key => {
		const value = extraData[key]
		if (value !== undefined && value !== null) {
			formData.append(key, value)
		}
	})

	return uploadFile(url, formData, config)
}

/**
 * 下载文件
 */
export const downloadFile = async (url, params = {}, filename = '', config = {}) => {
	try {
		const requestUrl = ensureUrl(url)

		// 获取带 token 的请求头
		const headers = getHeaders(config.headers || {})

		const response = await request({
			url: requestUrl,
			method: 'get',
			params,
			responseType: 'blob',
			headers,
			...config
		})

		// 获取文件名
		let fileName = filename
		if (!fileName) {
			const contentDisposition = response.headers['content-disposition']
			if (contentDisposition) {
				// 匹配 filename 或 filename*
				let match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
				if (match && match[1]) {
					fileName = match[1].replace(/['"]/g, '')
					try {
						fileName = decodeURIComponent(fileName)
					} catch (e) {
						// 解码失败，使用原文件名
					}
				}

				// 处理 filename* 格式（UTF-8编码）
				if (!fileName) {
					match = contentDisposition.match(/filename\*=UTF-8''([^;]*)/)
					if (match && match[1]) {
						fileName = decodeURIComponent(match[1])
					}
				}
			}
		}

		if (!fileName) {
			fileName = `download_${Date.now()}`
		}

		// 检查是否是错误响应
		const blob = response.data
		const contentType = response.headers['content-type']

		// 如果返回的是 JSON 错误信息
		if (contentType && contentType.includes('application/json')) {
			const text = await blob.text()
			const errorData = JSON.parse(text)

			// 检查登录过期
			if (errorData.code === 202) {
				handleUnauthorized()
				return Promise.reject(errorData)
			}

			ElMessage.error(errorData.message || errorData.msg || '下载失败')
			return Promise.reject(errorData)
		}

		// 创建 blob 链接并下载
		const link = document.createElement('a')
		const blobUrl = window.URL.createObjectURL(blob)
		link.href = blobUrl
		link.download = fileName
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
		window.URL.revokeObjectURL(blobUrl)

		ElMessage.success('下载成功')
		return response.data
	} catch (error) {
		// 检查错误中的登录过期
		if (error && error.code === 202) {
			handleUnauthorized()
			return Promise.reject(error)
		}
		ElMessage.error('下载失败：' + (error.message || '未知错误'))
		return Promise.reject(error)
	}
}

/**
 * POST 方式下载文件
 */
export const downloadFileByPost = async (url, data = {}, filename = '', config = {}) => {
	try {
		const requestUrl = ensureUrl(url)

		// 获取带 token 的请求头
		const headers = getHeaders(config.headers || {})

		const response = await request({
			url: requestUrl,
			method: 'post',
			data,
			responseType: 'blob',
			headers,
			...config
		})

		let fileName = filename
		if (!fileName) {
			const contentDisposition = response.headers['content-disposition']
			if (contentDisposition) {
				let match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
				if (match && match[1]) {
					fileName = match[1].replace(/['"]/g, '')
					try {
						fileName = decodeURIComponent(fileName)
					} catch (e) {}
				}
			}
		}

		if (!fileName) {
			fileName = `download_${Date.now()}`
		}

		// 检查是否是错误响应
		const blob = response.data
		const contentType = response.headers['content-type']

		if (contentType && contentType.includes('application/json')) {
			const text = await blob.text()
			const errorData = JSON.parse(text)

			// 检查登录过期
			if (errorData.code === 202) {
				handleUnauthorized()
				return Promise.reject(errorData)
			}

			ElMessage.error(errorData.message || errorData.msg || '下载失败')
			return Promise.reject(errorData)
		}

		const link = document.createElement('a')
		const blobUrl = window.URL.createObjectURL(blob)
		link.href = blobUrl
		link.download = fileName
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
		window.URL.revokeObjectURL(blobUrl)

		ElMessage.success('下载成功')
		return response.data
	} catch (error) {
		// 检查错误中的登录过期
		if (error && error.code === 202) {
			handleUnauthorized()
			return Promise.reject(error)
		}
		ElMessage.error('下载失败：' + (error.message || '未知错误'))
		return Promise.reject(error)
	}
}

export default {
	get,
	post,
	put,
	patch,
	del,
	uploadFile,
	uploadSingleFile,
	uploadMultipleFiles,
	downloadFile,
	downloadFileByPost
}